import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';
import { Tooltip } from '@mui/material';

import useQuickSearch from '../../../../../hooks/useQuickSearch';
import QuickSearchToolbar from '../../../../components/QuickSearchToolbar';

import getColumns from './columns';
import {
  BLOCKED_ROW_CLASS,
  StyledDataGrid,
  StatusCell,
  EmptyState,
  Summary,
} from './styles';
import texts from './texts.json';

const MigratableWorkoutsTable = ({
  workouts,
  hasSourceCoach,
  migratableCount,
  selectedIds,
  onSelectionChange,
  isRunning,
}) => {
  const statusRenderCell = useCallback(({ row, value }) => {
    const blockingNames = (row.blockingExercises || [])
      .map((exercise) => exercise.name)
      .filter(Boolean);

    const cell = <StatusCell $isBlocked={!row.isMigratable}>{value}</StatusCell>;

    if (row.isMigratable || blockingNames.length === 0) {
      return cell;
    }

    return (
      <Tooltip title={format(texts.blockingExercisesTooltip, { names: blockingNames.join(', ') })}>
        {cell}
      </Tooltip>
    );
  }, []);

  const columns = useMemo(() => getColumns({ statusRenderCell }), [statusRenderCell]);

  const isRowSelectable = useCallback(
    ({ row }) => !isRunning && row.isMigratable,
    [isRunning],
  );

  const {
    filteredRows,
    toolbarProps,
  } = useQuickSearch(workouts, columns);

  if (!hasSourceCoach) {
    return <EmptyState>{texts.noCoachSelected}</EmptyState>;
  }

  if (workouts.length === 0) {
    return <EmptyState>{texts.emptyLibrary}</EmptyState>;
  }

  return (
    <>
      <Summary>
        {format(texts.summary, { migratable: migratableCount, total: workouts.length })}
      </Summary>
      <StyledDataGrid
        rows={filteredRows}
        columns={columns}
        checkboxSelection
        disableRowSelectionOnClick
        /*
          Quick search filters the rows out of the grid entirely, so without this a search
          silently drops everything selected outside the current filter.
        */
        keepNonExistentRowsSelected
        rowSelectionModel={selectedIds}
        onRowSelectionModelChange={onSelectionChange}
        /*
          The rule lives with the list rather than with the checkboxes, so selection
          cannot be enabled without it. Compliance is re-checked server-side regardless —
          this only stops the UI offering a choice that would be rejected.
        */
        isRowSelectable={isRowSelectable}
        getRowClassName={({ row }) => (row.isMigratable ? '' : BLOCKED_ROW_CLASS)}
        components={{
          Toolbar: QuickSearchToolbar,
        }}
        componentsProps={{
          toolbar: {
            ...toolbarProps,
            autoFocus: true,
            placeholder: texts.searchPlaceholder,
          },
        }}
      />
    </>
  );
};

MigratableWorkoutsTable.propTypes = {
  workouts: PropTypes.array.isRequired,
  hasSourceCoach: PropTypes.bool.isRequired,
  migratableCount: PropTypes.number.isRequired,
  selectedIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSelectionChange: PropTypes.func.isRequired,
  isRunning: PropTypes.bool.isRequired,
};

export default MigratableWorkoutsTable;
